import React from 'react';
import * as buildHelpers from './build';
import * as colourHelpers from './colours';
import * as dispatcherTypes from './dispatcherTypes.js';
import * as scenarios from './scenarios.js';
import * as pageTypes from './pageTypes.js';
import * as jobNames from './jobNames.js';
import * as actionTypes from './actionTypes';
import * as paramHelpers from './params';

export const COLUMN_TYPES = {
  ACTIONABLE: 'actionable',
  ICON: 'icon',
  TEXT: 'text'
};

/* Configurations for all the Regression pages
dispatcherType: The type of dispatcher jobs the page should pull
scenariosBy: Determines how the pie charts are organized
scenarioFeed: Object that determines what the different parts of the table displays
  scenarioTypeLabel: The label for the left-most column
  scenarioTypeFunc: The function used to determine what to extract from the scenario name
                    for the left-most column
  scenarioNameLabel: The label for the column one over from the left
  scenarioNameFunc: The function used to determine what to extract from the scenario name
                   for the column mentioned above
scenarioActions: The list of actions that each item in the table can perform
                  (All actions can be found in RegressionScenarioActionsContainer)
scenariosByRadioButton: Optional parameter -
                        creates radio buttons that can switch up the scenariosBy field
pieStatsTitlesFunc: Function that determines the titles that the pie charts adopt
productContext: {
  priorityOrder {array} : for tests that run with multiple products this will be used to determine
    the order when displaying product info. In places where only a single product context is shown
    ! it is intended that the first product will be treated as the default main product
}
*/

// Often used for day diff calculations, best if fetching history is back exactly 1 week
// since the pages are mostly accessed weekdays we want to fetch back into a weekday
// fetching back into a weekend day may cause issues
// * ensure this is taken into account where necessary
// ! Do not change without good reason
export const FETCH_DAYS = 150;

export const ARTIFACT_PAGE = {
  dispatcherJobName: jobNames.ARTIFACT_REGRESSION_DISPATCHER,
  dispatcherType: dispatcherTypes.ARTIFACTREGRESSION,
  filters: {},
  pageType: pageTypes.ARTIFACTREGRESSION,
  scenariosBy: scenarios.scenariosByPlatform,
  scenarioFeed: {
    scenarioTypeLabel: 'Platform',
    scenarioTypeFunc: ({ scenario }) => scenarios.getArtifactPlatform(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getArtifactScenarioName(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH,
      actionTypes.OPEN_S3_PATH,
      actionTypes.BREAK,
      actionTypes.OPEN_DIFF_VIEWER,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.SET_AS_BASELINE,
      actionTypes.BREAK,
      actionTypes.SHOW_HISTORY,
      actionTypes.BREAK,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER,
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [ buildHelpers.PRODUCTS.PROCESS ] }
};

export const EXPORT_PAGE = {
  dispatcherJobName: jobNames.EXPORT_DISPATCHER,
  dispatcherType: dispatcherTypes.EXPORTTESTS,
  filters: {},
  pageType: pageTypes.EXPORTTESTS,
  scenariosBy: scenarios.scenariosByJobType,
  scenarioFeed: {
    scenarioTypeLabel: 'Type',
    scenarioTypeFunc: ({ scenario }) => scenarios.getExportType(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getExportScenario(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH_EXPORT_TESTS,
      actionTypes.OPEN_S3_PATH,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER,
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [ buildHelpers.PRODUCTS.EXAMINE ] }
};

export const REMOTE_AQUIRE_REGRESSION = {
  dispatcherJobName: jobNames.REMOTE_AQUIRE_REGRESSION_DISPATCHER,
  dispatcherType: dispatcherTypes.REMOTEACQUIREREGRESSION,
  filters: {},
  pageType: pageTypes.REMOTEACQUIREREGRESSION,
  scenariosBy: scenarios.scenariosByPlatform,
  scenarioFeed: {
    scenarioTypeLabel: 'Machine Name',
    scenarioTypeFunc: ({ scenario }) => scenarios.getArtifactPlatform(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getArtifactScenarioName(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [] }
};

export const CASE_AMENDING = {
  dispatcherJobName: jobNames.CASE_AMENDING_DISPATCHER,
  dispatcherType: dispatcherTypes.CASEAMENDINGTESTS,
  filters: {},
  pageType: pageTypes.CASEAMENDINGTESTS,
  scenariosBy: scenarios.scenariosByImageName,
  scenarioFeed: {
    scenarioTypeLabel: 'Image Name',
    scenarioTypeFunc: ({ scenario }) => scenarios.getCaseAmendingImage(scenario.name),
    scenarioNameLabel: 'Job Type',
    scenarioNameFunc: ({ scenario }) => scenarios.getCaseAmendingJobType(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_LOG_FILE_PATH,
      actionTypes.COPY_DIFF_RESULT_PATH,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER
    ],
  scenariosByRadioButtons:
    [
      scenarios.scenariosByImageName,
      scenarios.scenariosByJobType
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [] }
};

export const DIFF_TOOL_PAGE = {
  dispatcherJobName: jobNames.DIFF_TOOL_REGRESSION_DISPATCHER,
  dispatcherType: dispatcherTypes.DIFFTOOLREGRESSION,
  filters: {},
  pageType: pageTypes.DIFFTOOLREGRESSION,
  scenariosBy: scenarios.scenariosByJobType,
  scenarioFeed: {
    scenarioTypeLabel: 'Diff Type',
    scenarioTypeFunc: ({ scenario }) => scenarios.getDiffType(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getDiffScenario(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH,
      actionTypes.OPEN_S3_PATH,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [] }
};

export const WHITE_PAGE = {
  dispatcherJobName: jobNames.UI_DISPATCHER,
  dispatcherType: dispatcherTypes.UITESTS,
  filters: {},
  pageType: pageTypes.UITESTS,
  scenariosBy: scenarios.scenariosByJobType,
  scenarioFeed: {
    scenarioTypeLabel: 'Product',
    scenarioTypeFunc: ({ scenario }) => scenarios.getUiProduct(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getUiScenario(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH,
      actionTypes.OPEN_S3_PATH,
      actionTypes.BREAK,
      actionTypes.VIEW_TEST_ARTIFACTS,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER
    ],
  pieStatsTitlesFunc: (scenariosByGroup) =>
    scenarios.getWhitePieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: {
    priorityOrder: [
      buildHelpers.PRODUCTS.PROCESS,
      buildHelpers.PRODUCTS.EXAMINE
    ]
  }
};

export const ACQUISITION_PAGE = {
  dispatcherType: dispatcherTypes.ACQUISITIONTESTS,
  filters: {},
  pageType: pageTypes.ACQUISITIONTESTS,
  scenariosBy: scenarios.scenariosByJobType,
  scenarioFeed: {
    scenarioTypeLabel: 'Type',
    scenarioTypeFunc: ({ scenario }) => scenario.name
  },
  scenarioActions:
    [actionTypes.OPEN_JOB_URL],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [] }
};

export const INSTALLER_PAGE = {
  dispatcherJobName: jobNames.INSTALLER_DISPATCHER,
  dispatcherType: dispatcherTypes.INSTALLERTESTS,
  filters: {},
  pageType: pageTypes.INSTALLERTESTS,
  scenariosBy: scenarios.scenariosByInstaller,
  scenarioFeed: {
    scenarioTypeLabel: 'Product',
    scenarioTypeFunc: ({ scenario }) => scenarios.getUiProduct(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getUiScenario(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH,
      actionTypes.OPEN_S3_PATH,
      actionTypes.COPY_TREE_FILE_PATH,
      actionTypes.BREAK,
      actionTypes.VIEW_TEST_ARTIFACTS,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER,
    ],
  pieStatsTitlesFunc: (scenariosByGroup) =>
    scenarios.getWhitePieStatsTitle(scenariosByGroup),
  productContext: { priorityOrder: [] }
};

export const CASE_MERGING = {
  dispatcherJobName: jobNames.CASE_MERGING_DISPATCHER,
  dispatcherType: dispatcherTypes.CASEMERGINGTESTS,
  filters: {},
  pageType: pageTypes.CASEMERGINGTESTS,
  scenariosBy: scenarios.scenariosByImageName,
  scenarioFeed: {
    scenarioTypeLabel: 'Scenario',
    scenarioTypeFunc: ({ scenario }) => scenario.name,
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_LOG_FILE_PATH,
      actionTypes.COPY_DIFF_RESULT_PATH,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER
    ],
  pieStatsTitlesFunc: (scenariosByGroup) => scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: { priorityOrder: [ buildHelpers.PRODUCTS.PROCESS ] }
};

export const AXIOM_UI_PAGE = {
  dispatcherJobName: jobNames.AXIOM_UI_DISPATCHER,
  dispatcherType: dispatcherTypes.AXIOMUITESTS,
  filters: {},
  pageType: pageTypes.AXIOMUITESTS,
  scenariosBy: scenarios.scenariosByPlatform,
  scenarioFeed: {
    scenarioTypeLabel: 'Test Directory',
    scenarioTypeFunc: ({ scenario }) => scenario.name.split(':')[0],
    scenarioNameLabel: 'Test File',
    scenarioNameFunc: ({ scenario }) => scenario.name.split(':')[1]
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.OPEN_S3_PATH,
      actionTypes.COPY_FILE_PATH,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO

    ],
  pieStatsTitlesFunc: (scenariosByGroup) =>
    scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: {
    priorityOrder: [
      buildHelpers.PRODUCTS.PROCESS,
      buildHelpers.PRODUCTS.EXAMINE,
    ]
  }
};

export const TEST_STUDIO_PAGE = {
  dispatcherJobName: jobNames.TEST_STUDIO_DISPATCHER,
  dispatcherType: dispatcherTypes.TESTSTUDIO,
  filters: {},
  pageType: pageTypes.TESTSTUDIO,
  scenariosBy: scenarios.scenariosByJobType,
  scenarioFeed: {
    scenarioTypeLabel: 'Product',
    scenarioTypeFunc: ({ productName }) => productName,
    scenarioSecondaryTypeLabel: 'Test Project',
    scenarioSecondaryTypeFunc: ({ scenario }) => scenarios.getUiProduct(scenario.name),
    scenarioNameLabel: 'Scenario',
    scenarioNameFunc: ({ scenario }) => scenarios.getUiScenario(scenario.name)
  },
  scenarioActions:
    [
      actionTypes.BREAK,
      actionTypes.ADD_COMMENT,
      actionTypes.SHOW_COMMENTS,
      actionTypes.BREAK,
      actionTypes.OPEN_JOB_URL,
      actionTypes.COPY_FILE_PATH,
      actionTypes.OPEN_S3_PATH,
      actionTypes.BREAK,
      actionTypes.VIEW_TEST_ARTIFACTS,
      actionTypes.BREAK,
      actionTypes.RETRIGGER_SCENARIO,
      actionTypes.KEEP_FOREVER,
      actionTypes.DONT_KEEP_FOREVER
    ],
  pieStatsTitlesFunc: (scenariosByGroup) =>
    scenarios.getPieStatsTitle(scenariosByGroup),
  currentDispatcherTestsOnly: true,
  concerningTrendingOnly: false,
  productContext: {
    priorityOrder: [
      buildHelpers.PRODUCTS.PROCESS,
      buildHelpers.PRODUCTS.EXAMINE,
    ]
  }
};

export const REGRESSION_BUILD_TYPES = [
  'Prod',
  'Prebuild',
  'Topic'
];

export const filterScenario = (scenarioProductVersions, isTopic, filters) => {
  const isPrebuild = Object.values(scenarioProductVersions).some(v => v.startsWith('777'));
  let include = true;
  if (filters['Prod']) { include &= isPrebuild; }
  if (filters['Prebuild']) { include &= !isPrebuild ; }
  if (filters['Topic']) { include &= !isTopic ; }
  return include;
};

/**
 * Determines which colour to display for a scenario table cell
 * @param {*} arg single object for deferencing required context
 * @returns A single colour
 */
export const getTrendingResultCellColour = ({ numColumns, columnIndex, scenario }) => {
  // use scenario status to determine colour
  const matchedTrendingResult = scenario && scenario.trendingResults[numColumns - 1 - columnIndex];
  const matchedStatus = matchedTrendingResult && matchedTrendingResult.status;
  const info = matchedStatus && scenarios.SCENARIO_STATE_INFO[matchedStatus] ||
    scenarios.SCENARIO_STATE_INFO[scenarios.SCENARIO_UNKNOWN];

  // if found then return, else treat as N/A
  return info ?
    colourHelpers.colours[info.colour] :
    colourHelpers.colours.grey;
};

const getTrendingResultValue = (scenarioContext, trendIndex, productContext) => {
  const result = scenarioContext.trendingResults[trendIndex];
  if (!result) {
    return 'SCENARIO UNDEFINED';
  }
  const orderedVersions = productContext.priorityOrder.map(productName => {
    const relatedParamName = paramHelpers.PRODUCT_TO_VERSION_PARAM_MAP[productName];
    return result.productVersions[relatedParamName];
  }).filter(x => x);
  return orderedVersions[0] || 'N/A';
};

const getTrendingResultTooltip = (scenarioContext, trendIndex, productContext) => {
  if (!scenarioContext.trendingResults[trendIndex]) {
    return 'Not enough data';
  }

  const trendResult = scenarioContext.trendingResults[trendIndex];

  const orderedVersions = productContext.priorityOrder.map(productName => {
    const relatedParamName = paramHelpers.PRODUCT_TO_VERSION_PARAM_MAP[productName];
    const version = trendResult.productVersions[relatedParamName];
    if (!version) {
      return undefined;
    }
    return `${relatedParamName} :\n    ${trendResult.productVersions[relatedParamName]}`;
  }).filter(x => x);
  return (
    <p style={{ 'white-space': 'pre-wrap' }}>
      {[
        ...orderedVersions,
        `Dispatcher :\n    ${scenarioContext.dispatcherNameNumber}`,
        `JobId :\n    ${trendResult.runner}`,
        `UpdatedAt :\n    ${trendResult.updatedAt}`
      ].join('\n')}
    </p>
  );
};

export const trendingTableRecentRunHeaders = ({ productContext }) => {
  const recentRunHeaders = [
    {
      headerTitle: '5th Most Recent Run',
      colourFunc: context => getTrendingResultCellColour(context),
      valueType: COLUMN_TYPES.TEXT,
      valueFunc: ({ scenario }) => getTrendingResultValue(scenario, 4, productContext),
      onClick: ({ dispatchableAction, scenario, pageType }) => {
        // expecting jumpToScenario action
        const trendResult = scenario && scenario.trendingResults && scenario.trendingResults[4];
        dispatchableAction({
          dispatcherNameNumber: trendResult.dispatchNameNumber,
          pageType
        });
      },
      tooltipFunc: ({ scenario }) => getTrendingResultTooltip(scenario, 4, productContext)
    },
    {
      headerTitle: '4th Most Recent Run',
      colourFunc: context => getTrendingResultCellColour(context),
      valueType: COLUMN_TYPES.TEXT,
      valueFunc: ({ scenario }) => getTrendingResultValue(scenario, 3, productContext),
      onClick: ({ dispatchableAction, scenario, pageType }) => {
        // expecting jumpToScenario action
        const trendResult = scenario && scenario.trendingResults && scenario.trendingResults[3];
        dispatchableAction({
          dispatcherNameNumber: trendResult.dispatchNameNumber,
          pageType
        });
      },
      tooltipFunc: ({ scenario }) => getTrendingResultTooltip(scenario, 3, productContext)
    },
    {
      headerTitle: '3rd Most Recent Run',
      colourFunc: context => getTrendingResultCellColour(context),
      valueType: COLUMN_TYPES.TEXT,
      valueFunc: ({ scenario }) => getTrendingResultValue(scenario, 2, productContext),
      onClick: ({ dispatchableAction, scenario, pageType }) => {
        // expecting jumpToScenario action
        const trendResult = scenario && scenario.trendingResults && scenario.trendingResults[2];
        dispatchableAction({
          dispatcherNameNumber: trendResult.dispatchNameNumber,
          pageType
        });
      },
      tooltipFunc: ({ scenario }) => getTrendingResultTooltip(scenario, 2, productContext)
    },
    {
      headerTitle: '2nd Most Recent Run',
      colourFunc: context => getTrendingResultCellColour(context),
      valueType: COLUMN_TYPES.TEXT,
      valueFunc: ({ scenario }) => getTrendingResultValue(scenario, 1, productContext),
      onClick: ({ dispatchableAction, scenario, pageType }) => {
        // expecting jumpToScenario action
        const trendResult = scenario && scenario.trendingResults && scenario.trendingResults[1];
        dispatchableAction({
          dispatcherNameNumber: trendResult.dispatchNameNumber,
          pageType
        });
      },
      tooltipFunc: ({ scenario }) => getTrendingResultTooltip(scenario, 1, productContext)
    },
    {
      headerTitle: 'Most Recent Run',
      colourFunc: context => getTrendingResultCellColour(context),
      valueType: COLUMN_TYPES.TEXT,
      valueFunc: ({ scenario }) => getTrendingResultValue(scenario, 0, productContext),
      onClick: ({ dispatchableAction, scenario, pageType }) => {
        // expecting jumpToScenario action
        const trendResult = scenario && scenario.trendingResults && scenario.trendingResults[0];
        dispatchableAction({
          dispatcherNameNumber: trendResult.dispatchNameNumber,
          pageType
        });
      },
      tooltipFunc: ({ scenario }) => getTrendingResultTooltip(scenario, 0, productContext)
    }
  ];

  return recentRunHeaders;
};

/**
 * The page definitions starting from the top of the page are assumed to be complete
 * and can be considered fairly static overall once in place.
 * * As such code across the app should feel safe to lean on these assumptions
 *
 * This method is meant to be a safe guard to devs in case they break these assumptions.
*/
const validateRegressionPageDefinitions = (pageDefinition) => {
  console.assert(
    pageDefinition.productContext.priorityOrder.length > 0,
    `productContext.priorityOrder for page ${pageDefinition.prettyName} cannot be empty`
  );
};

// Do the validation on load
[
  TEST_STUDIO_PAGE
].forEach(pageDefinition => {
  validateRegressionPageDefinitions(pageDefinition);
});


// WEBPACK FOOTER //
// src/helpers/regressionPages.js